import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  ArrowLeft, Save, Check, RotateCcw, Plus, Trash2, Eye, AlertTriangle,
  Bot, BookOpen, Clock, MessageSquare, Users, Zap, ChevronDown, Loader2,
  Sparkles, FlaskConical, Send, User, Bell, MessageCircle, Play,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  getAutomationConfig, saveAutomationConfig, resetAutomationConfig,
  previewAutomationMessage, sandboxAiReply, sandboxReminders, sandboxSequence,
} from "./api";
import FlowHelper from "./FlowHelper";
import "./automation.css";

const TABS = [
  { key: "conversations", label: "Conversations", icon: Bot, sub: "AI Closer · Knowledge Base · Welcome & quick replies" },
  { key: "sequences", label: "Sequences", icon: Clock, sub: "Pre-demo reminders · Post-demo nudges · Ghost win-backs" },
  { key: "alerts", label: "Alerts & Fallback", icon: AlertTriangle, sub: "Admin alerts · Safety fallback for AI errors" },
  { key: "lab", label: "Test Lab", icon: FlaskConical, sub: "Sandbox to dry-run any rule" },
];

const SUB_TABS = {
  conversations: [
    { key: "ai", label: "AI Closer" },
    { key: "kb", label: "Knowledge Base" },
    { key: "quick", label: "Welcome & Quick Replies" },
  ],
  sequences: [
    { key: "pre", label: "Pre-demo" },
    { key: "post", label: "Post-demo" },
    { key: "ghost", label: "Ghosts & Nudges" },
  ],
};

// ── Reusable: textarea with variable picker inline ──
function VarTextarea({ value, onChange, variables, rows = 4, placeholder = "", testId }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const insert = (key) => {
    const ta = ref.current;
    if (!ta) {
      onChange((value || "") + "{" + key + "}");
      setOpen(false);
      return;
    }
    const start = ta.selectionStart ?? (value || "").length;
    const end = ta.selectionEnd ?? start;
    const insertion = "{" + key + "}";
    const next = (value || "").slice(0, start) + insertion + (value || "").slice(end);
    onChange(next);
    setOpen(false);
    // restore cursor after insertion
    setTimeout(() => {
      try {
        ta.focus();
        const pos = start + insertion.length;
        ta.setSelectionRange(pos, pos);
      } catch {}
    }, 0);
  };

  const runPreview = async () => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const r = await previewAutomationMessage(value || "");
      setPreview(r.rendered || "");
    } catch (e) {
      setPreview("(preview failed)");
    } finally {
      setLoadingPreview(false);
    }
  };

  return (
    <div className="auto-var-wrap">
      <textarea
        ref={ref}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="auto-textarea"
        data-testid={testId}
      />
      <div className="auto-var-toolbar">
        <div className="auto-var-picker-wrap">
          <button
            type="button"
            className="auto-var-btn"
            onClick={() => setOpen((o) => !o)}
            data-testid={`${testId}-var-btn`}
          >
            <Sparkles className="w-3.5 h-3.5" /> Insert variable <ChevronDown className="w-3.5 h-3.5" />
          </button>
          {open && (
            <div className="auto-var-menu" data-testid={`${testId}-var-menu`}>
              {variables.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  className="auto-var-item"
                  onClick={() => insert(v.key)}
                  data-testid={`var-item-${v.key}`}
                >
                  <code>{"{" + v.key + "}"}</code>
                  <span className="auto-var-item-label">{v.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button type="button" className="auto-var-btn" onClick={runPreview} data-testid={`${testId}-preview-btn`}>
          {loadingPreview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
          Preview
        </button>
      </div>
      {preview !== null && (
        <div className="auto-preview" data-testid={`${testId}-preview`}>
          <span className="auto-preview-label">Rendered:</span>
          <pre className="auto-preview-text">{preview}</pre>
        </div>
      )}
    </div>
  );
}

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      className={`auto-toggle ${checked ? "auto-toggle-on" : ""}`}
      onClick={() => onChange(!checked)}
      data-testid={testId}
    >
      <span className="auto-toggle-thumb" />
    </button>
  );
}

export default function Automation() {
  const navigate = useNavigate();
  const ctx = useOutletContext() || {};
  const [config, setConfig] = useState(null);
  const [variables, setVariables] = useState([]);
  // Default tab is now a supergroup; sub-tabs live within it
  const [tab, setTab] = useState("conversations");
  const [subTab, setSubTab] = useState({ conversations: "ai", sequences: "pre" });

  const setSub = (group, key) => setSubTab((s) => ({ ...s, [group]: key }));
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    getAutomationConfig()
      .then((r) => {
        setConfig(r.config);
        setVariables(r.variables || []);
      })
      .catch(() => {});
  }, []);

  const update = (updater) => {
    setConfig((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await saveAutomationConfig(config);
      setConfig(r.config);
      setSavedAt(Date.now());
      setDirty(false);
    } catch (e) {
      alert("Save failed: " + (e.message || "error"));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetOpen(false);
    setSaving(true);
    try {
      const r = await resetAutomationConfig();
      setConfig(r.config);
      setSavedAt(Date.now());
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  if (!config) {
    return (
      <div className="auto-loading" data-testid="auto-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading automation config...
      </div>
    );
  }

  return (
    <div className="auto-page" data-testid="automation-page">
      {/* Header */}
      <div className="auto-header auto-header-slim">
        <div className="auto-header-title">
          <h1>Automation Studio</h1>
          <span className="auto-data-source">Persists to <code>demo_settings.waAutomationConfig</code> · changes apply instantly without redeploy.</span>
        </div>
        <div className="auto-header-actions">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setResetOpen(true)}
            data-testid="auto-reset-btn"
            className="auto-reset-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !dirty}
            className="auto-save-btn"
            data-testid="auto-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> :
              savedAt && !dirty ? <><Check className="w-4 h-4" /> Saved</> :
              <><Save className="w-4 h-4" /> Save all</>}
          </Button>
        </div>
      </div>

      {/* Main switch: global enable */}
      <div className="auto-global-row" data-testid="auto-global-enable">
        <div>
          <strong>Automation engine</strong>
          <span>Turn off to pause ALL WhatsApp automation (reminders, nudges, sequences, AI replies).</span>
        </div>
        <Toggle
          checked={config.enabled !== false}
          onChange={(v) => update((c) => ({ ...c, enabled: v }))}
          testId="toggle-automation-enabled"
        />
      </div>

      {/* Tabs (supergroups) */}
      <div className="auto-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              className={`auto-tab ${active ? "auto-tab-active" : ""}`}
              onClick={() => setTab(t.key)}
              data-testid={`auto-tab-${t.key}`}
              title={t.sub}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Sub-tabs (only when applicable) */}
      {SUB_TABS[tab] && (
        <div className="auto-subtabs" data-testid={`auto-subtabs-${tab}`}>
          {SUB_TABS[tab].map((st) => (
            <button
              key={st.key}
              className={`auto-subtab ${subTab[tab] === st.key ? "auto-subtab-active" : ""}`}
              onClick={() => setSub(tab, st.key)}
              data-testid={`auto-subtab-${st.key}`}
            >
              {st.label}
            </button>
          ))}
        </div>
      )}

      {/* Body */}
      <div className="auto-body">
        {tab === "conversations" && subTab.conversations === "ai" && <AICloserTab config={config} update={update} variables={variables} />}
        {tab === "conversations" && subTab.conversations === "kb" && <KnowledgeBaseTab config={config} update={update} />}
        {tab === "conversations" && subTab.conversations === "quick" && <QuickRepliesTab config={config} update={update} />}
        {tab === "sequences" && subTab.sequences === "pre" && <PreDemoTab config={config} update={update} variables={variables} />}
        {tab === "sequences" && subTab.sequences === "post" && <PostDemoTab config={config} update={update} variables={variables} />}
        {tab === "sequences" && subTab.sequences === "ghost" && <GhostsTab config={config} update={update} variables={variables} />}
        {tab === "alerts" && <AlertsTab config={config} update={update} variables={variables} />}
        {tab === "lab" && <TestLabTab />}
      </div>

      {resetOpen && (
        <div className="auto-modal-overlay" onClick={() => setResetOpen(false)}>
          <div className="auto-modal" onClick={(e) => e.stopPropagation()} data-testid="reset-modal">
            <h3>Reset to factory defaults?</h3>
            <p>This wipes ALL your custom messages, timings, AI prompt, and knowledge base. Cannot be undone.</p>
            <div className="auto-modal-actions">
              <Button variant="outline" onClick={() => setResetOpen(false)} data-testid="reset-cancel-btn">Cancel</Button>
              <Button onClick={handleReset} data-testid="reset-confirm-btn" className="auto-danger-btn">
                Yes, reset everything
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Flow Helper — always accessible, outside tabs */}
      <FlowHelper />
    </div>
  );
}

// ── AI Closer Tab ──
function AICloserTab({ config, update, variables }) {
  const ai = config.aiCloser || {};
  const setAi = (patch) => update((c) => ({ ...c, aiCloser: { ...(c.aiCloser || {}), ...patch } }));

  return (
    <div className="auto-tab-body" data-testid="tab-ai">
      <div className="auto-card">
        <div className="auto-card-head">
          <h3>Core AI behaviour</h3>
          <Toggle checked={ai.enabled !== false} onChange={(v) => setAi({ enabled: v })} testId="toggle-ai-enabled" />
        </div>
        <p className="auto-card-sub">The AI reads the full conversation before replying. Tweak its persona, rules, and model here.</p>
        <div className="auto-row-2">
          <div className="auto-field">
            <Label>Persona name</Label>
            <Input
              value={ai.personaName || ""}
              onChange={(e) => setAi({ personaName: e.target.value })}
              placeholder="e.g. Shubham's AI assistant"
              data-testid="ai-persona-name"
            />
          </div>
          <div className="auto-field">
            <Label>Persona role</Label>
            <Input
              value={ai.personaRole || ""}
              onChange={(e) => setAi({ personaRole: e.target.value })}
              placeholder="e.g. Sales closer for BulkJobApply"
              data-testid="ai-persona-role"
            />
          </div>
        </div>
        <div className="auto-row-2">
          <div className="auto-field">
            <Label>Model</Label>
            <select
              className="auto-select"
              value={ai.model || "gpt-4o-mini"}
              onChange={(e) => setAi({ model: e.target.value })}
              data-testid="ai-model"
            >
              <option value="gpt-4o-mini">gpt-4o-mini (fast, cheap)</option>
              <option value="gpt-4o">gpt-4o (smarter, slower)</option>
              <option value="gpt-4.1">gpt-4.1</option>
              <option value="gpt-4.1-mini">gpt-4.1-mini</option>
            </select>
          </div>
          <div className="auto-field">
            <Label>Conversation history (messages back)</Label>
            <Input
              type="number"
              min={5} max={50}
              value={ai.historyMessages || 20}
              onChange={(e) => setAi({ historyMessages: parseInt(e.target.value || "20") })}
              data-testid="ai-history"
            />
            <span className="auto-hint">How many past messages to feed the AI (prevents "random" replies).</span>
          </div>
        </div>
        <div className="auto-field">
          <Label>Handoff keywords (comma-separated)</Label>
          <Input
            value={ai.handoffKeywords || ""}
            onChange={(e) => setAi({ handoffKeywords: e.target.value })}
            placeholder="refund,legal,lawyer,scam"
            data-testid="ai-handoff-keywords"
          />
          <span className="auto-hint">If a lead message contains any of these, hand over to a human closer instead of replying.</span>
        </div>
      </div>

      <div className="auto-card">
        <h3>Custom rules (injected into AI system prompt)</h3>
        <p className="auto-card-sub">Short bullet rules the AI MUST follow. Use this to fix "wrong time" or "random" replies.</p>
        <VarTextarea
          value={ai.customRules}
          onChange={(v) => setAi({ customRules: v })}
          variables={variables}
          rows={8}
          placeholder="- Never offer slots outside Mon-Sat 8am-2pm EST.&#10;- Always confirm timezone (IST or EST) before booking.&#10;- If unsure, set handoff_to_closer=true."
          testId="ai-custom-rules"
        />
      </div>

      <div className="auto-card">
        <h3>System prompt template</h3>
        <p className="auto-card-sub">Advanced — the skeleton the AI sees. Variables like <code>{"{conversation_history}"}</code>, <code>{"{lead_status}"}</code>, <code>{"{knowledge_base}"}</code> are replaced at runtime.</p>
        <VarTextarea
          value={ai.systemPrompt}
          onChange={(v) => setAi({ systemPrompt: v })}
          variables={variables}
          rows={16}
          testId="ai-system-prompt"
        />
      </div>
    </div>
  );
}

// ── Knowledge Base Tab ──
function KnowledgeBaseTab({ config, update }) {
  const kb = (config.aiCloser || {}).knowledgeBase || [];
  const setKb = (nextKb) => update((c) => ({ ...c, aiCloser: { ...(c.aiCloser || {}), knowledgeBase: nextKb } }));

  const addItem = () => {
    setKb([...kb, { id: `kb_${Date.now()}`, title: "New fact", content: "", enabled: true }]);
  };
  const updateItem = (idx, patch) => {
    const next = [...kb]; next[idx] = { ...next[idx], ...patch }; setKb(next);
  };
  const removeItem = (idx) => {
    const next = kb.filter((_, i) => i !== idx); setKb(next);
  };

  return (
    <div className="auto-tab-body" data-testid="tab-kb">
      <div className="auto-kb-info">
        <BookOpen className="w-4 h-4" />
        <div>
          <strong>The AI reads these facts on every reply.</strong>
          <span>Keep answers accurate & current — pricing, demo link, guarantees, payment. Each item is injected into the system prompt.</span>
        </div>
      </div>

      <div className="auto-kb-list">
        {kb.map((item, idx) => (
          <div key={item.id || idx} className={`auto-kb-item ${item.enabled === false ? "auto-kb-disabled" : ""}`} data-testid={`kb-item-${idx}`}>
            <div className="auto-kb-item-head">
              <Input
                value={item.title || ""}
                onChange={(e) => updateItem(idx, { title: e.target.value })}
                placeholder="Fact title (e.g. Pricing, Demo link)"
                className="auto-kb-title-input"
                data-testid={`kb-title-${idx}`}
              />
              <Toggle
                checked={item.enabled !== false}
                onChange={(v) => updateItem(idx, { enabled: v })}
                testId={`kb-toggle-${idx}`}
              />
              <button className="auto-icon-btn auto-danger-icon" onClick={() => removeItem(idx)} data-testid={`kb-delete-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <textarea
              value={item.content || ""}
              onChange={(e) => updateItem(idx, { content: e.target.value })}
              placeholder="Enter the fact/knowledge the AI should know..."
              className="auto-textarea"
              rows={4}
              data-testid={`kb-content-${idx}`}
            />
          </div>
        ))}
      </div>

      <Button variant="outline" onClick={addItem} className="auto-add-btn" data-testid="kb-add-btn">
        <Plus className="w-4 h-4" /> Add knowledge item
      </Button>
    </div>
  );
}

// ── Pre-Demo Reminders Tab ──
function PreDemoTab({ config, update, variables }) {
  const rules = config.preDemoReminders || [];
  const noShow = config.noShow || {};

  const setRules = (next) => update((c) => ({ ...c, preDemoReminders: next }));
  const setNoShow = (patch) => update((c) => ({ ...c, noShow: { ...(c.noShow || {}), ...patch } }));

  const addRule = () => {
    setRules([
      ...rules,
      { id: `r_${Date.now()}`, enabled: true, hoursBefore: 2, windowHours: 0.3, templateName: "", textMessage: "Hey {name}! Demo in 2h.", sendImage: false },
    ]);
  };
  const updateRule = (idx, patch) => { const next = [...rules]; next[idx] = { ...next[idx], ...patch }; setRules(next); };
  const removeRule = (idx) => setRules(rules.filter((_, i) => i !== idx));

  return (
    <div className="auto-tab-body" data-testid="tab-pre">
      <div className="auto-info-box">
        <AlertTriangle className="w-4 h-4" />
        <span>Reminders fire automatically before a booked demo. Use templates for &gt;24h window, text for inside 24h.</span>
      </div>

      <div className="auto-list">
        {rules.map((r, idx) => (
          <div key={r.id || idx} className={`auto-rule-card ${!r.enabled ? "auto-rule-disabled" : ""}`} data-testid={`pre-rule-${idx}`}>
            <div className="auto-rule-head">
              <div className="auto-rule-head-left">
                <Clock className="w-4 h-4" />
                <strong>{r.hoursBefore}h before demo</strong>
              </div>
              <Toggle checked={r.enabled !== false} onChange={(v) => updateRule(idx, { enabled: v })} testId={`pre-toggle-${idx}`} />
              <button className="auto-icon-btn auto-danger-icon" onClick={() => removeRule(idx)} data-testid={`pre-delete-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="auto-row-3">
              <div className="auto-field">
                <Label>Hours before demo</Label>
                <Input type="number" step="0.1" value={r.hoursBefore} onChange={(e) => updateRule(idx, { hoursBefore: parseFloat(e.target.value) || 0 })} data-testid={`pre-hours-${idx}`} />
              </div>
              <div className="auto-field">
                <Label>Window (± hours)</Label>
                <Input type="number" step="0.05" value={r.windowHours || 0.5} onChange={(e) => updateRule(idx, { windowHours: parseFloat(e.target.value) || 0.5 })} data-testid={`pre-window-${idx}`} />
              </div>
              <div className="auto-field">
                <Label>Approved Meta template (optional)</Label>
                <Input value={r.templateName || ""} onChange={(e) => updateRule(idx, { templateName: e.target.value })} placeholder="e.g. demo_reminder_1h" data-testid={`pre-tmpl-${idx}`} />
              </div>
            </div>
            <div className="auto-field">
              <Label>Free-form message</Label>
              <VarTextarea
                value={r.textMessage || ""}
                onChange={(v) => updateRule(idx, { textMessage: v })}
                variables={variables}
                rows={3}
                testId={`pre-msg-${idx}`}
              />
            </div>
          </div>
        ))}
      </div>

      <Button variant="outline" onClick={addRule} className="auto-add-btn" data-testid="pre-add-btn">
        <Plus className="w-4 h-4" /> Add reminder
      </Button>

      {/* No-Show block */}
      <div className="auto-card auto-noshow-card">
        <div className="auto-card-head">
          <h3>No-show follow-up</h3>
          <Toggle checked={noShow.enabled !== false} onChange={(v) => setNoShow({ enabled: v })} testId="noshow-toggle" />
        </div>
        <p className="auto-card-sub">Fired once if the lead didn't show up — between minHoursAfter and maxHoursAfter.</p>
        <div className="auto-row-3">
          <div className="auto-field">
            <Label>Min hours after demo</Label>
            <Input type="number" step="0.1" value={noShow.minHoursAfter || 1} onChange={(e) => setNoShow({ minHoursAfter: parseFloat(e.target.value) || 1 })} data-testid="noshow-min" />
          </div>
          <div className="auto-field">
            <Label>Max hours after demo</Label>
            <Input type="number" step="0.5" value={noShow.maxHoursAfter || 24} onChange={(e) => setNoShow({ maxHoursAfter: parseFloat(e.target.value) || 24 })} data-testid="noshow-max" />
          </div>
          <div className="auto-field">
            <Label>Auto-mark "no show" after N hours</Label>
            <Input type="number" step="0.5" value={noShow.autoMarkAfterHours || 2} onChange={(e) => setNoShow({ autoMarkAfterHours: parseFloat(e.target.value) || 2 })} data-testid="noshow-automark" />
          </div>
        </div>
        <div className="auto-field">
          <Label>Meta template name (optional, for past-24h-window)</Label>
          <Input value={noShow.templateName || ""} onChange={(e) => setNoShow({ templateName: e.target.value })} placeholder="e.g. demo_noshow" data-testid="noshow-tmpl" />
        </div>
        <div className="auto-field">
          <Label>Text fallback</Label>
          <VarTextarea
            value={noShow.textMessage || ""}
            onChange={(v) => setNoShow({ textMessage: v })}
            variables={variables}
            rows={3}
            testId="noshow-msg"
          />
        </div>
      </div>
    </div>
  );
}

// ── Post-Demo Sequence Tab ──
function PostDemoTab({ config, update, variables }) {
  const steps = config.postDemoSequence || [];
  const setSteps = (next) => update((c) => ({ ...c, postDemoSequence: next }));
  const updateStep = (idx, patch) => { const next = [...steps]; next[idx] = { ...next[idx], ...patch }; setSteps(next); };
  const removeStep = (idx) => setSteps(steps.filter((_, i) => i !== idx));
  const addStep = () => {
    const nextStep = (steps.length ? Math.max(...steps.map((s) => s.step || 0)) : 0) + 1;
    setSteps([...steps, { step: nextStep, enabled: true, dayAfterDemo: nextStep * 2, label: `Step ${nextStep}`, templateName: "", textMessage: "Hey {name}! ...", sendPdf: false }]);
  };

  return (
    <div className="auto-tab-body" data-testid="tab-post">
      <div className="auto-info-box">
        <MessageSquare className="w-4 h-4" />
        <span>Auto-sent to leads after a demo. Pauses if the lead replies. Day 0 ships a personalized PDF by default.</span>
      </div>

      <div className="auto-list">
        {steps.map((s, idx) => (
          <div key={s.step || idx} className={`auto-rule-card ${!s.enabled ? "auto-rule-disabled" : ""}`} data-testid={`post-step-${idx}`}>
            <div className="auto-rule-head">
              <div className="auto-rule-head-left">
                <span className="auto-step-badge">#{s.step}</span>
                <strong>{s.label || `Step ${s.step}`}</strong>
                <span className="auto-day-pill">Day {s.dayAfterDemo}</span>
              </div>
              <Toggle checked={s.enabled !== false} onChange={(v) => updateStep(idx, { enabled: v })} testId={`post-toggle-${idx}`} />
              <button className="auto-icon-btn auto-danger-icon" onClick={() => removeStep(idx)} data-testid={`post-delete-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="auto-row-3">
              <div className="auto-field">
                <Label>Day after demo</Label>
                <Input type="number" min={0} value={s.dayAfterDemo} onChange={(e) => updateStep(idx, { dayAfterDemo: parseInt(e.target.value || "0") })} data-testid={`post-day-${idx}`} />
              </div>
              <div className="auto-field">
                <Label>Label</Label>
                <Input value={s.label || ""} onChange={(e) => updateStep(idx, { label: e.target.value })} data-testid={`post-label-${idx}`} />
              </div>
              <div className="auto-field">
                <Label>Meta template (for &gt;24h window)</Label>
                <Input value={s.templateName || ""} onChange={(e) => updateStep(idx, { templateName: e.target.value })} placeholder="e.g. followup_immediate" data-testid={`post-tmpl-${idx}`} />
              </div>
            </div>
            <div className="auto-field">
              <Label>Free-form message</Label>
              <VarTextarea
                value={s.textMessage || ""}
                onChange={(v) => updateStep(idx, { textMessage: v })}
                variables={variables}
                rows={4}
                testId={`post-msg-${idx}`}
              />
            </div>
            <div className="auto-row-2">
              <label className="auto-checkbox-row">
                <input
                  type="checkbox"
                  checked={!!s.sendPdf}
                  onChange={(e) => updateStep(idx, { sendPdf: e.target.checked })}
                  data-testid={`post-pdf-${idx}`}
                />
                <span>Attach personalized PDF report</span>
              </label>
            </div>
            {s.sendPdf && (
              <div className="auto-field">
                <Label>PDF caption (sent with the PDF)</Label>
                <VarTextarea
                  value={s.pdfCaption || ""}
                  onChange={(v) => updateStep(idx, { pdfCaption: v })}
                  variables={variables}
                  rows={2}
                  testId={`post-pdfcap-${idx}`}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <Button variant="outline" onClick={addStep} className="auto-add-btn" data-testid="post-add-btn">
        <Plus className="w-4 h-4" /> Add step
      </Button>
    </div>
  );
}

// ── Ghosts / Nudges Tab ──
function GhostsTab({ config, update, variables }) {
  const newLeadNudges = config.newLeadNudges || [];
  const ghostNudges = config.ghostNudges || [];
  const delay = config.delayFollowUp || {};

  const setNewLead = (next) => update((c) => ({ ...c, newLeadNudges: next }));
  const setGhost = (next) => update((c) => ({ ...c, ghostNudges: next }));
  const setDelay = (patch) => update((c) => ({ ...c, delayFollowUp: { ...(c.delayFollowUp || {}), ...patch } }));

  const renderList = (items, setItems, timeField, timeLabel, testPrefix, addDefault) => (
    <>
      <div className="auto-list">
        {items.map((n, idx) => (
          <div key={n.id || idx} className={`auto-rule-card ${!n.enabled ? "auto-rule-disabled" : ""}`} data-testid={`${testPrefix}-item-${idx}`}>
            <div className="auto-rule-head">
              <div className="auto-rule-head-left">
                <Clock className="w-4 h-4" />
                <strong>{n[timeField]}h</strong>
              </div>
              <Toggle checked={n.enabled !== false} onChange={(v) => { const next = [...items]; next[idx] = { ...n, enabled: v }; setItems(next); }} testId={`${testPrefix}-toggle-${idx}`} />
              <button className="auto-icon-btn auto-danger-icon" onClick={() => setItems(items.filter((_, i) => i !== idx))} data-testid={`${testPrefix}-delete-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="auto-row-2">
              <div className="auto-field">
                <Label>{timeLabel}</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={n[timeField]}
                  onChange={(e) => { const next = [...items]; next[idx] = { ...n, [timeField]: parseFloat(e.target.value) || 0 }; setItems(next); }}
                  data-testid={`${testPrefix}-hours-${idx}`}
                />
              </div>
            </div>
            <div className="auto-field">
              <Label>Message</Label>
              <VarTextarea
                value={n.message || ""}
                onChange={(v) => { const next = [...items]; next[idx] = { ...n, message: v }; setItems(next); }}
                variables={variables}
                rows={3}
                testId={`${testPrefix}-msg-${idx}`}
              />
            </div>
          </div>
        ))}
      </div>
      <Button
        variant="outline"
        onClick={() => setItems([...items, addDefault()])}
        className="auto-add-btn"
        data-testid={`${testPrefix}-add-btn`}
      >
        <Plus className="w-4 h-4" /> Add nudge
      </Button>
    </>
  );

  return (
    <div className="auto-tab-body" data-testid="tab-ghost">
      <h3 className="auto-section-h">New-lead nudges <span>— fire when a new inbound lead hasn't replied</span></h3>
      {renderList(newLeadNudges, setNewLead, "hoursAfterCreated", "Hours after lead created", "nln", () => ({ id: `nln_${Date.now()}`, enabled: true, hoursAfterCreated: 6, message: "Hey {name}! Still there?" }))}

      <h3 className="auto-section-h" style={{ marginTop: 32 }}>Mid-conversation ghost nudges <span>— when the lead stopped replying after 2+ exchanges</span></h3>
      {renderList(ghostNudges, setGhost, "hoursSilent", "Hours silent", "ghost", () => ({ id: `g_${Date.now()}`, enabled: true, hoursSilent: 6, message: "Hey {name} — still interested?" }))}

      <h3 className="auto-section-h" style={{ marginTop: 32 }}>"Maybe later" delay follow-up</h3>
      <div className="auto-card">
        <div className="auto-card-head">
          <span>Fires once after N days when a lead said "maybe later"</span>
          <Toggle checked={delay.enabled !== false} onChange={(v) => setDelay({ enabled: v })} testId="delay-toggle" />
        </div>
        <div className="auto-field">
          <Label>Days after "maybe later"</Label>
          <Input type="number" min={1} value={delay.daysAfter || 14} onChange={(e) => setDelay({ daysAfter: parseInt(e.target.value || "14") })} data-testid="delay-days" />
        </div>
        <div className="auto-field">
          <Label>Message</Label>
          <VarTextarea
            value={delay.message || ""}
            onChange={(v) => setDelay({ message: v })}
            variables={variables}
            rows={3}
            testId="delay-msg"
          />
        </div>
      </div>
    </div>
  );
}

// ── Quick Replies Tab ──
function QuickRepliesTab({ config, update }) {
  const welcome = config.welcome || {};
  const setWelcome = (patch) => update((c) => ({ ...c, welcome: { ...(c.welcome || {}), ...patch } }));

  const qrs = config.quickReplies || [];
  const setQrs = (next) => update((c) => ({ ...c, quickReplies: next }));
  const addQr = () => setQrs([...qrs, { id: `q_${Date.now()}`, label: "New reply", text: "" }]);

  return (
    <div className="auto-tab-body" data-testid="tab-quick">
      <div className="auto-card">
        <div className="auto-card-head">
          <h3>Welcome message (first-time inbound)</h3>
          <Toggle checked={welcome.enabled !== false} onChange={(v) => setWelcome({ enabled: v })} testId="welcome-toggle" />
        </div>
        <p className="auto-card-sub">The first reply sent when a brand-new number messages you. Use <code>{"{name}"}</code> to personalize.</p>
        <div className="auto-field">
          <Label>Message</Label>
          <textarea
            value={welcome.message || ""}
            onChange={(e) => setWelcome({ message: e.target.value })}
            className="auto-textarea"
            rows={4}
            data-testid="welcome-msg"
          />
        </div>
        <div className="auto-field">
          <Label>Fallback Meta template (used if free-form send fails)</Label>
          <Input value={welcome.templateFallback || ""} onChange={(e) => setWelcome({ templateFallback: e.target.value })} data-testid="welcome-tmpl" />
        </div>
      </div>

      {/* Interactive WA reply buttons */}
      <div className="auto-card">
        <div className="auto-card-head">
          <h3>Interactive WhatsApp buttons</h3>
          <span className="auto-hint">Up to 3 tap-to-reply buttons shown under the welcome message. Title ≤ 20 chars.</span>
        </div>
        <p className="auto-card-sub">When a lead taps a button, the title text flows into the AI just like a normal reply — no extra wiring needed. Leave empty to send plain text.</p>
        <div className="auto-list">
          {(welcome.buttons || []).map((b, idx) => (
            <div key={b.id || idx} className={`auto-qr-row ${!b.enabled ? "auto-rule-disabled" : ""}`} data-testid={`welcome-btn-${idx}`}>
              <Input
                value={b.title || ""}
                onChange={(e) => {
                  const next = [...(welcome.buttons || [])]; next[idx] = { ...b, title: e.target.value.slice(0, 20) }; setWelcome({ buttons: next });
                }}
                placeholder="Button text (e.g. Book my demo)"
                className="auto-qr-label"
                maxLength={20}
                data-testid={`welcome-btn-title-${idx}`}
              />
              <Input
                value={b.id || ""}
                onChange={(e) => {
                  const next = [...(welcome.buttons || [])]; next[idx] = { ...b, id: e.target.value }; setWelcome({ buttons: next });
                }}
                placeholder="Button ID (e.g. BOOK_DEMO)"
                className="auto-qr-text"
                data-testid={`welcome-btn-id-${idx}`}
              />
              <Toggle
                checked={b.enabled !== false}
                onChange={(v) => {
                  const next = [...(welcome.buttons || [])]; next[idx] = { ...b, enabled: v }; setWelcome({ buttons: next });
                }}
                testId={`welcome-btn-toggle-${idx}`}
              />
              <button
                className="auto-icon-btn auto-danger-icon"
                onClick={() => setWelcome({ buttons: (welcome.buttons || []).filter((_, i) => i !== idx) })}
                data-testid={`welcome-btn-delete-${idx}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        {(welcome.buttons || []).length < 3 && (
          <Button
            variant="outline"
            onClick={() => setWelcome({
              buttons: [...(welcome.buttons || []), { id: `BTN_${Date.now()}`, title: "New button", enabled: true }],
            })}
            className="auto-add-btn"
            data-testid="welcome-btn-add"
          >
            <Plus className="w-4 h-4" /> Add button ({3 - (welcome.buttons || []).length} left)
          </Button>
        )}
        <div className="auto-field" style={{ marginTop: 12 }}>
          <Label>Footer text (optional, shown below buttons)</Label>
          <Input
            value={welcome.buttonsFooter || ""}
            onChange={(e) => setWelcome({ buttonsFooter: e.target.value })}
            placeholder="e.g. BulkJobApply • 93% success rate"
            maxLength={60}
            data-testid="welcome-btn-footer"
          />
        </div>
      </div>

      <div className="auto-card">
        <h3>Quick reply buttons (Inbox composer)</h3>
        <p className="auto-card-sub">One-click replies that show up in the Inbox chat composer.</p>
        <div className="auto-list">
          {qrs.map((q, idx) => (
            <div key={q.id || idx} className="auto-qr-row" data-testid={`qr-${idx}`}>
              <Input
                value={q.label || ""}
                onChange={(e) => { const next = [...qrs]; next[idx] = { ...q, label: e.target.value }; setQrs(next); }}
                placeholder="Button label (e.g. Book now)"
                className="auto-qr-label"
                data-testid={`qr-label-${idx}`}
              />
              <Input
                value={q.text || ""}
                onChange={(e) => { const next = [...qrs]; next[idx] = { ...q, text: e.target.value }; setQrs(next); }}
                placeholder="Full message text sent when clicked"
                className="auto-qr-text"
                data-testid={`qr-text-${idx}`}
              />
              <button className="auto-icon-btn auto-danger-icon" onClick={() => setQrs(qrs.filter((_, i) => i !== idx))} data-testid={`qr-delete-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <Button variant="outline" onClick={addQr} className="auto-add-btn" data-testid="qr-add-btn">
          <Plus className="w-4 h-4" /> Add quick reply
        </Button>
      </div>
    </div>
  );
}


// ── Admin Alerts & Safety Fallback Tab ──
function AlertsTab({ config, update, variables }) {
  const alertCfg = config.adminAlert || {};
  const safety = config.safetyFallback || {};
  const setAlert = (patch) => update((c) => ({ ...c, adminAlert: { ...(c.adminAlert || {}), ...patch } }));
  const setSafety = (patch) => update((c) => ({ ...c, safetyFallback: { ...(c.safetyFallback || {}), ...patch } }));

  const alertVars = [
    { key: "greeting", label: "Recipient greeting (e.g. ' — Shubham')", example: " — Shubham" },
    { key: "name", label: "Lead full name", example: "Priya Sharma" },
    { key: "phone", label: "Lead phone", example: "+91 9876543210" },
    { key: "source", label: "Lead source + headline", example: 'facebook_ad — "Get 10 interviews"' },
    { key: "first_msg_line", label: "First-msg line (auto formatted)", example: "• First msg: Hi interested\n" },
    { key: "ctwa_line", label: "CTWA attribution line", example: "• 🎯 CTWA ad click (attribution active)\n" },
  ];

  return (
    <div className="auto-tab-body" data-testid="tab-alerts">
      <div className="auto-info-box">
        <AlertTriangle className="w-4 h-4" />
        <span>These messages are triggered automatically by the system — not by leads. Edit the copy or turn them off entirely.</span>
      </div>

      {/* Admin alert */}
      <div className="auto-card">
        <div className="auto-card-head">
          <h3>Admin new-lead alert</h3>
          <Toggle checked={alertCfg.enabled !== false} onChange={(v) => setAlert({ enabled: v })} testId="alert-admin-toggle" />
        </div>
        <p className="auto-card-sub">
          Sent to every recipient in <code>demo_settings.adminNotifyPhones</code> when a fresh inbound lead arrives.
          Use the placeholders below (these are admin-alert-specific, different from the lead-message variables).
        </p>
        <div className="auto-field">
          <Label>Alert body</Label>
          <VarTextarea
            value={alertCfg.message || ""}
            onChange={(v) => setAlert({ message: v })}
            variables={alertVars}
            rows={8}
            testId="alert-body"
          />
        </div>
        <div className="auto-row-3">
          <div className="auto-field">
            <Label>Recipient greeting template</Label>
            <Input
              value={alertCfg.greetingTemplate || ""}
              onChange={(e) => setAlert({ greetingTemplate: e.target.value })}
              placeholder=" — {recipient_name}"
              data-testid="alert-greeting"
            />
          </div>
          <div className="auto-field">
            <Label>"First msg" line template</Label>
            <Input
              value={alertCfg.firstMsgLineTemplate || ""}
              onChange={(e) => setAlert({ firstMsgLineTemplate: e.target.value })}
              placeholder="• First msg: {first_msg}\n"
              data-testid="alert-firstmsg"
            />
          </div>
          <div className="auto-field">
            <Label>CTWA line template</Label>
            <Input
              value={alertCfg.ctwaLineTemplate || ""}
              onChange={(e) => setAlert({ ctwaLineTemplate: e.target.value })}
              placeholder="• 🎯 CTWA ad click (attribution active)\n"
              data-testid="alert-ctwa"
            />
          </div>
        </div>
      </div>

      {/* Safety fallback */}
      <div className="auto-card">
        <div className="auto-card-head">
          <h3>AI safety fallback</h3>
          <Toggle checked={safety.enabled !== false} onChange={(v) => setSafety({ enabled: v })} testId="safety-toggle" />
        </div>
        <p className="auto-card-sub">
          Sent automatically if the AI closer completely fails to reply (JSON parse error, timeout, etc.).
          Flags the lead for closer attention.
        </p>
        <div className="auto-field">
          <Label>Fallback message</Label>
          <VarTextarea
            value={safety.message || ""}
            onChange={(v) => setSafety({ message: v })}
            variables={variables}
            rows={3}
            testId="safety-msg"
          />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  AI TEST LAB — chat sandbox + time-travel reminders + sequence preview
//  Zero persistence, zero real WhatsApp sends. Uses /sandbox/* endpoints.
// ═══════════════════════════════════════════════════════════════════

const MODES = [
  { key: "chat", label: "AI Chat", icon: MessageCircle, hint: "Type as a lead → see real AI reply" },
  { key: "reminders", label: "Pre-Demo Reminders", icon: Bell, hint: "Time-travel to check which reminders fire" },
  { key: "sequence", label: "Post-Demo Sequence", icon: Play, hint: "Preview Day 0–7 follow-up messages" },
];

// helper: format <input type=datetime-local> values ↔ ISO-UTC
function toLocalInput(d) {
  // Returns YYYY-MM-DDTHH:mm in local timezone for <input type="datetime-local">
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToUtcIso(localStr) {
  if (!localStr) return null;
  return new Date(localStr).toISOString();
}

function TestLabTab() {
  const [mode, setMode] = useState("chat");
  const now = new Date();
  const defaultDemo = new Date(now.getTime() + 2 * 60 * 60 * 1000); // +2h

  // Shared lead state
  const [lead, setLead] = useState({
    clientName: "Priya",
    clientLastName: "Sharma",
    targetRole: "Software Engineer",
    targetMarket: "US",
    status: "new",
    recommendedPlan: "fullstack-2mo",
    monthsSearching: 3,
    weeklyApps: 10,
  });
  const [simNowLocal, setSimNowLocal] = useState(toLocalInput(now));
  const [demoLocal, setDemoLocal] = useState(toLocalInput(defaultDemo));

  const updateLead = (patch) => setLead((l) => ({ ...l, ...patch }));

  return (
    <div className="auto-tab-body" data-testid="tab-lab">
      <div className="auto-info-box" style={{ background: "rgba(58,123,213,0.07)", borderColor: "rgba(58,123,213,0.28)", color: "#9fd6f5" }}>
        <FlaskConical className="w-4 h-4" />
        <span><strong style={{ color: "#fff" }}>Test Lab.</strong> Test your prompt, knowledge base, timings & sequences against a pretend lead. Nothing is saved. Nothing is sent to real WhatsApp.</span>
      </div>

      {/* Shared lead panel */}
      <div className="auto-card">
        <h3>Pretend lead</h3>
        <p className="auto-card-sub">This is the lead data the AI and reminders will see. Change anything to test different scenarios.</p>
        <div className="auto-row-3">
          <div className="auto-field">
            <Label>First name</Label>
            <Input value={lead.clientName || ""} onChange={(e) => updateLead({ clientName: e.target.value })} data-testid="lab-name" />
          </div>
          <div className="auto-field">
            <Label>Target role</Label>
            <Input value={lead.targetRole || ""} onChange={(e) => updateLead({ targetRole: e.target.value })} data-testid="lab-role" />
          </div>
          <div className="auto-field">
            <Label>Target market</Label>
            <Input value={lead.targetMarket || ""} onChange={(e) => updateLead({ targetMarket: e.target.value })} data-testid="lab-market" />
          </div>
        </div>
        <div className="auto-row-3">
          <div className="auto-field">
            <Label>Status</Label>
            <select
              className="auto-select"
              value={lead.status}
              onChange={(e) => updateLead({ status: e.target.value })}
              data-testid="lab-status"
            >
              <option value="new">new (wants to book)</option>
              <option value="upcoming">upcoming (demo booked)</option>
              <option value="demo_done">demo_done (close the sale)</option>
              <option value="payment_sent">payment_sent</option>
              <option value="converted">converted (paid client)</option>
              <option value="no_show">no_show</option>
              <option value="cold">cold (went silent)</option>
            </select>
          </div>
          <div className="auto-field">
            <Label>Recommended plan</Label>
            <select
              className="auto-select"
              value={lead.recommendedPlan}
              onChange={(e) => updateLead({ recommendedPlan: e.target.value })}
              data-testid="lab-plan"
            >
              <option value="1-month-full-stack">Full Stack 1-month ($399)</option>
              <option value="2-month-full-stack">Full Stack 2-month ($625)</option>
              <option value="guaranteed">Guaranteed ($1,499)</option>
            </select>
          </div>
          <div className="auto-field">
            <Label>Simulated "current time"</Label>
            <input
              type="datetime-local"
              className="auto-date-input"
              value={simNowLocal}
              onChange={(e) => setSimNowLocal(e.target.value)}
              data-testid="lab-sim-now"
            />
            <span className="auto-hint">Pretend it's this time right now</span>
          </div>
        </div>
      </div>

      {/* Mode switcher */}
      <div className="auto-tabs" style={{ background: "transparent", border: 0, padding: 0, gap: 8 }}>
        {MODES.map((m) => {
          const Icon = m.icon;
          return (
            <button
              key={m.key}
              className={`auto-tab ${mode === m.key ? "auto-tab-active" : ""}`}
              onClick={() => setMode(m.key)}
              data-testid={`lab-mode-${m.key}`}
              title={m.hint}
            >
              <Icon className="w-4 h-4" /> {m.label}
            </button>
          );
        })}
      </div>

      {mode === "chat" && <ChatSandbox lead={lead} simNowLocal={simNowLocal} demoLocal={demoLocal} setDemoLocal={setDemoLocal} />}
      {mode === "reminders" && <ReminderSandbox lead={lead} simNowLocal={simNowLocal} demoLocal={demoLocal} setDemoLocal={setDemoLocal} />}
      {mode === "sequence" && <SequenceSandbox lead={lead} />}
    </div>
  );
}

// ── Chat sandbox ──
function ChatSandbox({ lead, simNowLocal, demoLocal, setDemoLocal }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = (input || "").trim();
    if (!text || sending) return;
    setError(null);
    const userMsg = { role: "lead", body: text };
    const nextMsgs = [...messages, userMsg];
    setMessages(nextMsgs);
    setInput("");
    setSending(true);
    try {
      const body = {
        lead: {
          ...lead,
          scheduledAt: ["upcoming", "demo_done", "no_show"].includes(lead.status) ? localInputToUtcIso(demoLocal) : null,
          demoDoneAt: ["demo_done", "payment_sent", "converted"].includes(lead.status)
            ? new Date(new Date(localInputToUtcIso(simNowLocal)).getTime() - 3600000).toISOString()
            : null,
        },
        userMessage: text,
        simulatedNow: localInputToUtcIso(simNowLocal),
        history: nextMsgs.map((m) => ({ direction: m.role === "lead" ? "incoming" : "outgoing", body: m.body })),
      };
      const r = await sandboxAiReply(body);
      setMessages((m) => [
        ...m,
        {
          role: "ai",
          body: r.reply || "(no reply)",
          meta: {
            intent: r.intent,
            booking: r.booking_detected,
            bookingDate: r.booking_date,
            bookingTz: r.booking_timezone,
            handoff: r.handoff_to_closer,
            latency: r.latency_ms,
            model: r.model,
          },
        },
      ]);
    } catch (e) {
      setError(e.message || "AI call failed");
    } finally {
      setSending(false);
    }
  };

  const reset = () => { setMessages([]); setError(null); };

  return (
    <div className="auto-card">
      <div className="auto-card-head">
        <h3>AI Chat Sandbox</h3>
        <Button variant="outline" size="sm" onClick={reset} data-testid="lab-chat-reset" disabled={sending || messages.length === 0}>
          <RotateCcw className="w-3.5 h-3.5" /> Clear chat
        </Button>
      </div>
      {["upcoming", "demo_done", "no_show"].includes(lead.status) && (
        <div className="auto-field">
          <Label>Demo scheduled at (used when status=upcoming/demo_done)</Label>
          <input
            type="datetime-local"
            className="auto-date-input"
            value={demoLocal}
            onChange={(e) => setDemoLocal(e.target.value)}
            data-testid="lab-demo-at"
          />
        </div>
      )}

      <div className="lab-chat-window" ref={scrollRef} data-testid="lab-chat-window">
        {messages.length === 0 && (
          <div className="lab-chat-empty">
            <Bot className="w-7 h-7" style={{ opacity: 0.5 }} />
            <span>Type a message as the lead — the AI will reply using your current prompt + knowledge base.</span>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`lab-msg lab-msg-${m.role}`} data-testid={`lab-msg-${i}`}>
            <div className="lab-msg-avatar">
              {m.role === "lead" ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
            </div>
            <div className="lab-msg-body">
              <div className="lab-msg-text">{m.body}</div>
              {m.meta && (
                <div className="lab-msg-meta">
                  <span className={`lab-meta-pill lab-intent-${m.meta.intent}`}>{m.meta.intent}</span>
                  {m.meta.booking && (
                    <span className="lab-meta-pill lab-meta-booking">
                      📅 booking {m.meta.bookingDate || ""} {m.meta.bookingTz || ""}
                    </span>
                  )}
                  {m.meta.handoff && <span className="lab-meta-pill lab-meta-handoff">👤 handoff</span>}
                  <span className="lab-meta-dim">{m.meta.latency}ms · {m.meta.model}</span>
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="lab-msg lab-msg-ai lab-msg-typing">
            <div className="lab-msg-avatar"><Bot className="w-3.5 h-3.5" /></div>
            <div className="lab-msg-body">
              <div className="lab-typing"><span /><span /><span /></div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="lab-error" data-testid="lab-chat-error">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="lab-chat-input">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder='Type as the lead — e.g. "Can I book tomorrow 3pm IST?"'
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          disabled={sending}
          data-testid="lab-chat-input"
        />
        <Button onClick={send} disabled={sending || !input.trim()} data-testid="lab-chat-send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send</>}
        </Button>
      </div>
    </div>
  );
}

// ── Reminder sandbox ──
function ReminderSandbox({ lead, simNowLocal, demoLocal, setDemoLocal }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true); setResult(null);
    try {
      const r = await sandboxReminders({
        lead: { ...lead, status: "upcoming", scheduledAt: localInputToUtcIso(demoLocal) },
        simulatedNow: localInputToUtcIso(simNowLocal),
      });
      setResult(r);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auto-card">
      <div className="auto-card-head">
        <h3>Pre-Demo Reminder Time-Travel</h3>
      </div>
      <p className="auto-card-sub">Set "simulated now" above + "demo scheduled at" below, then check which reminder would fire at that moment.</p>
      <div className="auto-row-2">
        <div className="auto-field">
          <Label>Demo scheduled at</Label>
          <input
            type="datetime-local"
            className="auto-date-input"
            value={demoLocal}
            onChange={(e) => setDemoLocal(e.target.value)}
            data-testid="lab-rem-demo-at"
          />
        </div>
        <div className="auto-field" style={{ justifyContent: "flex-end" }}>
          <Label style={{ visibility: "hidden" }}>-</Label>
          <Button onClick={run} disabled={loading} data-testid="lab-rem-run" className="auto-save-btn" style={{ alignSelf: "flex-start" }}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4" /> Check which reminders fire</>}
          </Button>
        </div>
      </div>

      {result && (
        <div className="lab-result" data-testid="lab-rem-result">
          <div className="lab-summary">
            <span><strong>Hours until demo:</strong> {result.hoursUntilDemo}h</span>
            <span className="auto-hint">(simulatedNow: {new Date(result.simulatedNowUtc).toLocaleString()})</span>
          </div>
          <div className="lab-result-list">
            {(result.outputs || []).map((o) => (
              <div key={o.id} className={`lab-result-card ${o.willFire ? "lab-fire-yes" : "lab-fire-no"}`} data-testid={`lab-rem-${o.id}`}>
                <div className="lab-result-head">
                  <div>
                    <span className={`lab-fire-badge ${o.willFire ? "lab-fire-badge-yes" : "lab-fire-badge-no"}`}>
                      {o.willFire ? "✓ FIRES" : "✗ skipped"}
                    </span>
                    <strong style={{ marginLeft: 10 }}>{o.kind === "no_show" ? "No-show follow-up" : `${o.hoursBefore}h pre-demo`}</strong>
                  </div>
                  {o.templateName && <code className="lab-tmpl-pill">tmpl: {o.templateName}</code>}
                </div>
                <div className="lab-rule-reason">{o.reason}</div>
                <div className="lab-rendered">{o.message}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sequence sandbox ──
function SequenceSandbox({ lead }) {
  const [days, setDays] = useState(3);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true); setResult(null);
    try {
      const r = await sandboxSequence({
        lead: { ...lead, status: "demo_done" },
        daysSinceDemo: Number(days),
      });
      setResult(r);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auto-card">
      <div className="auto-card-head">
        <h3>Post-Demo Sequence Preview</h3>
      </div>
      <p className="auto-card-sub">Slide the "days since demo" and see which Day 0/1/3/5/7 message would be sent right now.</p>
      <div className="auto-row-2">
        <div className="auto-field">
          <Label>Days since demo: <strong style={{ color: "#9fd6f5" }}>{days}</strong></Label>
          <input
            type="range"
            min={0}
            max={14}
            step={0.5}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className="lab-slider"
            data-testid="lab-seq-slider"
          />
          <div className="lab-slider-ticks">
            {[0, 1, 3, 5, 7, 10, 14].map((d) => <span key={d}>{d}d</span>)}
          </div>
        </div>
        <div className="auto-field" style={{ justifyContent: "flex-end" }}>
          <Button onClick={run} disabled={loading} data-testid="lab-seq-run" className="auto-save-btn" style={{ alignSelf: "flex-start", marginTop: 24 }}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4" /> Preview sequence</>}
          </Button>
        </div>
      </div>

      {result && (
        <div className="lab-result" data-testid="lab-seq-result">
          <div className="lab-summary">
            <span><strong>Days since demo:</strong> {result.daysSinceDemo}</span>
            <span className="auto-hint">Steps marked "FIRES" = would have been sent by now</span>
          </div>
          <div className="lab-result-list">
            {(result.outputs || []).map((o) => (
              <div key={o.step} className={`lab-result-card ${o.willFire ? "lab-fire-yes" : "lab-fire-no"}`} data-testid={`lab-seq-step-${o.step}`}>
                <div className="lab-result-head">
                  <div>
                    <span className={`lab-fire-badge ${o.willFire ? "lab-fire-badge-yes" : "lab-fire-badge-no"}`}>
                      {o.willFire ? "✓ FIRES" : "not yet"}
                    </span>
                    <strong style={{ marginLeft: 10 }}>Step {o.step}: {o.label}</strong>
                    <span className="auto-day-pill" style={{ marginLeft: 8 }}>Day {o.dayAfterDemo}</span>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {o.sendPdf && <code className="lab-tmpl-pill">📎 PDF</code>}
                    {o.templateName && <code className="lab-tmpl-pill">tmpl: {o.templateName}</code>}
                  </div>
                </div>
                {o.pdfCaption && <div className="lab-rule-reason">PDF caption: {o.pdfCaption}</div>}
                <div className="lab-rendered">{o.message}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

